import { buildAIPrompt, SYMPTOM_NAMES } from './aiPromptTemplate';

/**
 * Build context summary from hangover relief survey answers
 */
export function buildHangoverContext(answers) {
    const contextParts = [];

    // Safety check
    if (answers.safety) {
        if (answers.safety.includes('none')) {
            contextParts.push('- Good health condition, no specific risks');
        } else {
            contextParts.push('- Some health concerns to note');
        }
    }

    // Demographics
    if (answers.demographics) {
        const { age, weight } = answers.demographics;
        if (age) contextParts.push(`- Age group: ${age}`);
        if (weight) contextParts.push(`- Weight: ${weight}`);
    }

    // Main concern
    if (answers.mainConcern) {
        const concernMap = {
            'regular_drinker': 'Regular drinker, liver care',
            'hangover': 'Hangover discomfort',
            'occasional': 'Occasional social drinking',
            'reduce': 'Want to reduce drinking'
        };
        contextParts.push(`- Main concern: ${concernMap[answers.mainConcern]}`);
    }

    // 分支A：经常喝酒
    if (answers.drinkingFrequency) {
        const freqMap = {
            'daily': 'Drink daily',
            '4-6_week': '4-6 times a week',
            '2-3_week': '2-3 times a week'
        };
        contextParts.push(`- Drinking frequency: ${freqMap[answers.drinkingFrequency]}`);
    }

    if (answers.alcoholType && answers.alcoholType.length > 0) {
        const types = answers.alcoholType.map(t => {
            switch (t) {
                case 'beer': return 'Beer';
                case 'wine': return 'Wine';
                case 'spirits': return 'Spirits';
                case 'cocktails': return 'Cocktails';
                default: return t;
            }
        });
        contextParts.push(`- Mainly drink: ${types.join(', ')}`);
    }

    if (answers.drinkingAmount) {
        const amountMap = {
            '1-2': '1-2 drinks per session',
            '3-4': '3-4 drinks per session',
            '5+': '5+ drinks per session'
        };
        contextParts.push(`- ${amountMap[answers.drinkingAmount]}`);
    }

    if (answers.afterEffects && answers.afterEffects.length > 0) {
        const effects = answers.afterEffects
            .filter(e => e !== 'none')
            .map(e => {
                switch (e) {
                    case 'dry_mouth': return 'Dry mouth/inflammation';
                    case 'fatigue': return 'Fatigue next day';
                    case 'stomach': return 'Stomach discomfort';
                    case 'sleep': return 'Poor sleep quality';
                    default: return e;
                }
            });
        if (effects.length > 0) {
            contextParts.push(`- After effects: ${effects.join(', ')}`);
        }
    }

    // 分支B：宿醉
    if (answers.hangoverSymptoms && answers.hangoverSymptoms.length > 0) {
        const symptoms = answers.hangoverSymptoms.map(s => {
            switch (s) {
                case 'headache': return 'Headache/dizziness';
                case 'nausea': return 'Nausea';
                case 'fatigue': return 'Weakness';
                case 'dehydration': return 'Severe thirst/dehydration';
                case 'brain_fog': return 'Brain fog';
                case 'vertigo': return 'Vertigo';
                default: return s;
            }
        });
        contextParts.push(`- Hangover symptoms: ${symptoms.join(', ')}`);
    }

    if (answers.hangoverDuration) {
        const durationMap = {
            '2-4hrs': 'Lasts 2-4 hours',
            'half_day': 'Lasts half a day',
            'full_day': 'Lasts full day or more'
        };
        contextParts.push(`- ${durationMap[answers.hangoverDuration]}`);
    }

    if (answers.hangoverFrequency) {
        const freqMap = {
            'weekly': 'Drink weekly',
            'monthly': 'Drink monthly',
            'occasional': 'Drink occasionally'
        };
        contextParts.push(`- Drinking frequency: ${freqMap[answers.hangoverFrequency]}`);
    }

    // 分支C：偶尔聚会
    if (answers.socialFrequency) {
        const freqMap = {
            '2-4_month': 'Socialize 2-4 times/month',
            '1_month': 'Socialize ~1 time/month',
            'rare': 'Socialize rarely'
        };
        contextParts.push(`- ${freqMap[answers.socialFrequency]}`);
    }

    if (answers.socialAlcoholType && answers.socialAlcoholType.length > 0) {
        const types = answers.socialAlcoholType.map(t => {
            switch (t) {
                case 'beer': return 'Beer';
                case 'wine': return 'Wine';
                case 'shots': return 'Shots';
                case 'cocktails': return 'Cocktails';
                default: return t;
            }
        });
        contextParts.push(`- Usually drink at parties: ${types.join(', ')}`);
    }

    if (answers.socialConcerns && answers.socialConcerns.length > 0) {
        const concerns = answers.socialConcerns.map(c => {
            switch (c) {
                case 'next_day': return 'Affecting work next day';
                case 'overdrink': return 'Drinking too much';
                case 'health': return 'Health/liver damage';
                case 'stomach': return 'Stomach discomfort';
                default: return c;
            }
        });
        contextParts.push(`- Main concerns: ${concerns.join(', ')}`);
    }

    // 分支D：减少饮酒
    if (answers.reduceGoal) {
        const goalMap = {
            'quit': 'Want to quit completely',
            'reduce': 'Want to reduce drinking',
            'improve': 'Want to improve comfort after drinking'
        };
        contextParts.push(`- Goal: ${goalMap[answers.reduceGoal]}`);
    }

    if (answers.currentDrinking) {
        const drinkingMap = {
            'daily': 'Currently drink daily',
            '3-5_week': 'Currently drink 3-5 times/week',
            '1-2_week': 'Currently drink 1-2 times/week'
        };
        contextParts.push(`- ${drinkingMap[answers.currentDrinking]}`);
    }

    if (answers.reduceChallenges && answers.reduceChallenges.length > 0) {
        const challenges = answers.reduceChallenges.map(c => {
            switch (c) {
                case 'social_pressure': return 'Social pressure';
                case 'habit': return 'Habitual drinking';
                case 'health_concern': return 'Health concerns';
                case 'sleep_energy': return 'Improve sleep/energy';
                default: return c;
            }
        });
        contextParts.push(`- Challenges: ${challenges.join(', ')}`);
    }

    // Constitution
    if (answers.constitution && answers.constitution.length > 0) {
        const constitution = answers.constitution
            .filter(c => c !== 'none')
            .map(c => {
                switch (c) {
                    case 'heat': return 'Prone to inflammation';
                    case 'thirsty': return 'Thirsty';
                    case 'sweating': return 'Easily sweating';
                    case 'cold_limbs': return 'Cold hands and feet';
                    default: return c;
                }
            });
        if (constitution.length > 0) {
            contextParts.push(`- Constitution: ${constitution.join(', ')}`);
        }
    }

    // Other symptoms
    if (answers.otherSymptoms && answers.otherSymptoms.length > 0) {
        const symptoms = answers.otherSymptoms
            .filter(s => s !== 'none')
            .map(s => {
                switch (s) {
                    case 'stomach': return 'Stomach issues';
                    case 'sleep': return 'Poor sleep quality';
                    case 'stress': return 'High stress/anxiety';
                    case 'digestion': return 'Poor digestion';
                    default: return s;
                }
            });
        if (symptoms.length > 0) {
            contextParts.push(`- Other symptoms: ${symptoms.join(', ')}`);
        }
    }

    // Liver check
    if (answers.liverCheck) {
        const checkMap = {
            'normal': 'Liver function normal',
            'elevated': 'Liver function markers elevated',
            'no_check': 'No liver function check'
        };
        contextParts.push(`- ${checkMap[answers.liverCheck]}`);
    }

    return contextParts.join('\n');
}

/**
 * Build AI prompt for hangover relief questions
 */
export function buildHangoverPrompt(question, answers, userName) {
    const context = buildHangoverContext(answers);
  return buildAIPrompt(SYMPTOM_NAMES.hangover, context, question.text, userName);
}